import {
    RECEIVE_OFFERINGS,
    RECEIVE_OFFERING,
    OFFERING_UPDATE,
    type FetchOfferingsAction,
    type FetchOfferingAction,
    type OfferingUpdateAction,
    type MatchesState,
} from "@atg-sport-shared/pool-types";

import {MATCH_UPDATE} from "./matches.actions";
import {updateMatchFromLiveData} from "./matches.helpers";

import type {MatchUpdateAction} from "./matches.actions";
import type {Match} from "@atg-sport-shared/pool-types/matchTypes";
import type {Offering} from "@atg-sport-shared/pool-types/offeringTypes";
import type {Reducer, AnyAction} from "redux";

const initialState: MatchesState = {};

const matchesToObject = ({
    matches,
    offeringId,
    existingMatches,
}: {
    matches: ReadonlyArray<Match>;
    offeringId: string;
    existingMatches: MatchesState;
}) =>
    matches.reduce((previousMatches, currentMatch) => {
        const existingMatch = existingMatches[currentMatch.id];
        const nextBetDistributionByOffering = {
            ...(existingMatch?.betDistributionByOffering ?? {}),
            ...(currentMatch.betDistribution
                ? {[offeringId]: currentMatch.betDistribution}
                : {}),
        };

        return {
            ...previousMatches,
            [currentMatch.id]: {
                ...currentMatch,
                liveData: currentMatch.liveData ?? existingMatch?.liveData,
                betDistributionByOffering:
                    Object.keys(nextBetDistributionByOffering).length > 0
                        ? nextBetDistributionByOffering
                        : undefined,
                sportradar: {
                    ...currentMatch.sportradar,
                    matchId: (() => {
                        const eventIdParts =
                            currentMatch.sportradar.eventId?.split(":") ?? [];
                        return eventIdParts.length === 3 ? eventIdParts[2] ?? null : null;
                    })(),
                },
            },
        };
    }, {});

export const matchesReducer: Reducer<MatchesState> = (
    state: MatchesState = initialState,
    _action: AnyAction,
) => {
    const action = _action as
        | FetchOfferingsAction
        | FetchOfferingAction
        | OfferingUpdateAction
        | MatchUpdateAction;

    switch (action.type) {
        case RECEIVE_OFFERINGS:
        case RECEIVE_OFFERING: {
            if (
                !action.error &&
                action.payload instanceof Array &&
                action.payload.length
            ) {
                return action.payload.reduce(
                    (nextState: MatchesState, offering: Offering) => {
                        const data = matchesToObject({
                            matches: offering.matches,
                            offeringId: offering.id,
                            existingMatches: nextState,
                        });

                        return {...nextState, ...data};
                    },
                    state,
                );
            }
            return state;
        }
        case OFFERING_UPDATE: {
            const {
                offering: {id: offeringId, matches},
            } = action.payload;

            const updatedMatches = matchesToObject({
                matches,
                offeringId,
                existingMatches: state,
            });

            return {
                ...state,
                ...updatedMatches,
            };
        }
        case MATCH_UPDATE: {
            const {type: updateType, match: newMatchData} = action.payload;

            const matchToUpdate: Match | undefined = state[newMatchData.id];

            if (!matchToUpdate) {
                return state;
            }

            const updatedMatch = updateMatchFromLiveData({
                matchToUpdate,
                updateType,
                newMatchData,
            });

            return {
                ...state,
                [matchToUpdate.id]: updatedMatch,
            };
        }

        default: {
            return state;
        }
    }
};
